import React, { useState, useEffect } from "react";
import * as api from "../../services/api";
import "./UsersManagement.css";

export default function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [pincodeFilter, setPincodeFilter] = useState("");
  const [uniquePincodes, setUniquePincodes] = useState([]);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchQuery, pincodeFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersData = await api.getAllUsers();
      
      // Debug: Log all users received
      console.log('=== USERS FROM API ===');
      console.log('Total users received:', usersData?.length || 0);
      
      if (usersData && usersData.length > 0) {
        usersData.forEach((user, index) => {
          console.log(`User ${index + 1}:`, {
            id: user._id,
            email: user.email || '(no email)',
            phone: user.phone || '(no phone)',
            role: user.role || '(no role)',
            name: user.name || '(no name)',
            addressesCount: user.addresses?.length || 0
          });
        });
        
        const adminCount = usersData.filter(u => u.role === 'admin').length;
        const userCount = usersData.filter(u => !u.role || u.role === 'user').length;
        console.log(`Admins: ${adminCount}, Regular users: ${userCount}`);
      } else {
        console.warn('No users data received from API');
      }
      
      // Ensure we have an array
      const validUsers = Array.isArray(usersData) ? usersData : [];
      setUsers(validUsers);
      setFilteredUsers(validUsers);
      
      // Extract unique pincodes
      const pincodes = new Set();
      usersData.forEach(user => {
        user.addresses?.forEach(address => {
          const pincode = address.pincode || address.postalCode;
          if (pincode) {
            pincodes.add(pincode);
          }
        });
      });
      setUniquePincodes(Array.from(pincodes).sort());
    } catch (error) {
      console.error('Error loading users:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load users';
      const errorStatus = error.response?.status;
      console.error('Error details:', {
        message: errorMessage,
        status: errorStatus,
        url: error.config?.url,
        response: error.response?.data
      });
      alert(`Failed to load users: ${errorMessage}${errorStatus ? ` (Status: ${errorStatus})` : ''}`);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(user => {
        const nameMatch = user.name?.toLowerCase().includes(query);
        const emailMatch = user.email?.toLowerCase().includes(query);
        const phoneMatch = user.phone?.includes(query);
        const roleMatch = user.role?.toLowerCase().includes(query) || 
                         (query === 'admin' && user.role?.toLowerCase() === 'admin') ||
                         (query === 'user' && (!user.role || user.role?.toLowerCase() === 'user'));
        
        // Search in addresses
        const addressMatch = user.addresses?.some(address => {
          const streetMatch = address.street?.toLowerCase().includes(query) || 
                             address.line1?.toLowerCase().includes(query);
          const areaMatch = address.area?.toLowerCase().includes(query);
          const cityMatch = address.city?.toLowerCase().includes(query);
          const stateMatch = address.state?.toLowerCase().includes(query);
          const pincodeMatch = (address.pincode || address.postalCode)?.includes(query);
          const landmarkMatch = address.landmark?.toLowerCase().includes(query);
          
          return streetMatch || areaMatch || cityMatch || stateMatch || pincodeMatch || landmarkMatch;
        });

        return nameMatch || emailMatch || phoneMatch || roleMatch || addressMatch;
      });
    }

    // Apply pincode filter
    if (pincodeFilter) {
      filtered = filtered.filter(user => {
        return user.addresses?.some(address => {
          const pincode = address.pincode || address.postalCode;
          return pincode === pincodeFilter;
        });
      });
    }

    setFilteredUsers(filtered);
  };

  const formatAddress = (address) => {
    const parts = [];
    if (address.street || address.line1) parts.push(address.street || address.line1);
    if (address.area) parts.push(address.area);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.pincode || address.postalCode) parts.push(address.pincode || address.postalCode);
    return parts.join(", ") || "No address";
  };

  if (loading) {
    return <div className="users-loading">Loading users...</div>;
  }

  return (
    <div className="users-management">
      <div className="users-header">
        <h1>Users Management</h1>
        <div className="users-count">
          Total Users: {filteredUsers.length} / {users.length}
        </div>
      </div>

      <div className="users-filters">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by name, email, phone, address, pincode..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-box">
          <label htmlFor="pincode-filter">Filter by Pincode:</label>
          <select
            id="pincode-filter"
            value={pincodeFilter}
            onChange={(e) => setPincodeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Pincodes</option>
            {uniquePincodes.map(pincode => (
              <option key={pincode} value={pincode}>
                {pincode}
              </option>
            ))}
          </select>
          {pincodeFilter && (
            <button
              className="clear-filter-btn"
              onClick={() => setPincodeFilter("")}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="users-table-container">
        {filteredUsers.length === 0 ? (
          <div className="empty-state">
            <p>No users found matching your search criteria.</p>
          </div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Address</th>
                <th>Pincode</th>
                <th>Registered</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => {
                const primaryAddress = user.addresses?.[0] || {};
                const pincode = primaryAddress.pincode || primaryAddress.postalCode || "N/A";
                
                return (
                  <tr key={user._id}>
                    <td>{user.name || "N/A"}</td>
                    <td>{user.email || "N/A"}</td>
                    <td>{user.phone || "N/A"}</td>
                    <td>
                      <span className={`role-badge ${(user.role && user.role.toLowerCase()) === 'admin' ? 'admin' : 'user'}`}>
                        {(user.role && user.role.toLowerCase()) === 'admin' ? '👑 Admin' : '👤 User'}
                      </span>
                    </td>
                    <td className="address-cell">
                      {user.addresses && user.addresses.length > 0 ? (
                        <div className="addresses-list">
                          {user.addresses.map((address, index) => (
                            <div key={index} className="address-item">
                              <strong>{address.label || address.type || "Address"}:</strong> {formatAddress(address)}
                            </div>
                          ))}
                        </div>
                      ) : (
                        "No address"
                      )}
                    </td>
                    <td>
                      {user.addresses && user.addresses.length > 0 ? (
                        <div className="pincodes-list">
                          {user.addresses.map((address, index) => {
                            const addrPincode = address.pincode || address.postalCode;
                            return addrPincode ? (
                              <span key={index} className="pincode-badge">{addrPincode}</span>
                            ) : null;
                          })}
                        </div>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td>
                      {user.createdAt 
                        ? new Date(user.createdAt).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })
                        : "N/A"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="users-mobile-view">
        {filteredUsers.length === 0 ? (
          <div className="empty-state">
            <p>No users found matching your search criteria.</p>
          </div>
        ) : (
          filteredUsers.map((user) => {
            const primaryAddress = user.addresses?.[0] || {};
            const pincode = primaryAddress.pincode || primaryAddress.postalCode || "N/A";
            
            return (
              <div key={user._id} className="user-mobile-card">
                <div className="user-card-header">
                  <h3>{user.name || "N/A"}</h3>
                  <span className={`user-role ${(user.role && user.role.toLowerCase()) === 'admin' ? 'admin' : 'user'}`}>
                    {(user.role && user.role.toLowerCase()) === 'admin' ? '👑 Admin' : '👤 User'}
                  </span>
                </div>
                <div className="user-card-info">
                  <div className="info-item">
                    <span className="info-label">📧 Email:</span>
                    <span className="info-value">{user.email || "N/A"}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">📱 Phone:</span>
                    <span className="info-value">{user.phone || "N/A"}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">📍 Address:</span>
                    <div className="info-value">
                      {user.addresses && user.addresses.length > 0 ? (
                        <div className="addresses-list-mobile">
                          {user.addresses.map((address, index) => (
                            <div key={index} className="address-item-mobile">
                              <strong>{address.label || address.type || "Address"}:</strong> {formatAddress(address)}
                            </div>
                          ))}
                        </div>
                      ) : (
                        "No address"
                      )}
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-label">📮 Pincode:</span>
                    <div className="info-value">
                      {user.addresses && user.addresses.length > 0 ? (
                        <div className="pincodes-list-mobile">
                          {user.addresses.map((address, index) => {
                            const addrPincode = address.pincode || address.postalCode;
                            return addrPincode ? (
                              <span key={index} className="pincode-badge">{addrPincode}</span>
                            ) : null;
                          })}
                        </div>
                      ) : (
                        "N/A"
                      )}
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-label">📅 Registered:</span>
                    <span className="info-value">
                      {user.createdAt 
                        ? new Date(user.createdAt).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

